import axiosInstance from "../Component/common/AxiosInterceptor";
import {
  createRecord,
  updateRecord,
  deleteRecord,
  getRecord,
  getAllRecords,
  clearStore,
} from "../offline/IndexDBCrud";

export default class ApiService {
  static BASE_URL = process.env.REACT_APP_BASEURL;

  static getHeader() {
    return {
      "Content-Type": "application/json",
    };
  }

  static async saveToIndexedDB(keyPrefix, payload) {
    try {
      // Store the whole payload under a single key so the cached shape matches
      // the online API response shape. Use distinct keys for arrays vs single
      // objects to avoid fragmenting cached entries.
      if (Array.isArray(payload)) {
        const id = `${keyPrefix}-list`;
        await updateRecord("data", { id, type: keyPrefix, value: payload });
      } else if (payload && typeof payload === "object") {
        const item = payload;
        const id =
          item &&
          (item.id || item._id || item.bookingId || item.roomId || item.hotelId)
            ? `${keyPrefix}-${
                item.id ||
                item._id ||
                item.bookingId ||
                item.roomId ||
                item.hotelId
              }`
            : `${keyPrefix}-single`;
        await updateRecord("data", { id, type: keyPrefix, value: item });
      }
    } catch (e) {
      // non-fatal: don't block normal flow if saving fails
      console.warn("Failed to save to indexedDB", e);
    }
  }

  static async removeFromIndexedDB(keyPrefix, identifier) {
    try {
      if (typeof identifier !== "undefined" && identifier !== null) {
        const idKey = `${keyPrefix}-${identifier}`;
        await deleteRecord("data", idKey);
      } else {
        // if no identifier provided, delete all entries of this type
        const all = await getAllRecords("data");
        const toDelete = all.filter((r) => r && r.type === keyPrefix);
        await Promise.all(toDelete.map((r) => deleteRecord("data", r.id)));
      }
    } catch (e) {
      console.warn("Failed to remove from indexedDB", e);
    }
  }

  static async getFromIndexedDB(keyPrefix) {
    try {
      const all = await getAllRecords("data");
      const filtered = all
        .filter((r) => r && r.type === keyPrefix)
        .map((r) => r.value);
      if (!filtered || filtered.length === 0)
        return Array.isArray(filtered) ? [] : null;
      return filtered.length === 1 ? filtered[0] : filtered;
    } catch (e) {
      console.warn("Failed to read from indexedDB", e);
      return null;
    }
  }

  /**AUTH */
  static async registerUser(registration) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/auth/register`,
        registration
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  static async loginUser(loginDetails) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/auth/login`,
        loginDetails
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  static async forgotPassword(email) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/auth/forgot-password`,
        { email }
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  static async resetPassword(token, newPassword) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/auth/reset-password`,
        { token, newPassword }
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  /**USERS */
  static async getAllUsers() {
    try {
      const response = await axiosInstance.get(`${this.BASE_URL}/users/all`, {
        headers: this.getHeader(),
      });

      return response;
    } catch (error) {
      
      return [];
    }
  }

  static async getUsersByRole(role) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/users/all/${role}`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return [];
    }
  }

  static async getUserProfile() {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/users/get-logged-in-profile-info`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  static async getUser(userId) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/users/get-by-id/${userId}`,
        {
          headers: this.getHeader(),
        }
      );
      
      return response;
    } catch (error) {
      return null;
    }
  }

  static async getUserBookings(userId) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/users/get-user-bookings/${userId}`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return [];
    }
  }

  static async saveUser(values) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/users/save`,
        values,
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;
      return payload;
    } catch (error) {
      return null;
    }
  }

  static async updateUser(userId, values) {
    try {
      const updatedValues = { ...values, updatedAt: Date.now() };
      const response = await axiosInstance.put(
        `${this.BASE_URL}/users/update/${userId}`,
        updatedValues,
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;
      
      return payload;
    } catch (error) {
      console.log("Error:", error);
      return null;
    }
  }

  static async deleteUser(userId) {
    try {
      const response = await axiosInstance.delete(
        `${this.BASE_URL}/users/delete/${userId}`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  /**Hotel */
  static async fetchHotels() {
    const response = await axiosInstance.get(
        `${this.BASE_URL}/hotels/getAll`,
        {
          headers: this.getHeader(),
        }
      );
      const payload = response?.data ? response.data : response;
      return payload;
  }

  static async addHotel(formData) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/hotels/add`,
        formData,
        {
          headers: {
            ...this.getHeader(),
            "Content-Type": "multipart/form-data"
          },
        }
      );
      const payload = response && response.data ? response.data : response;
     
      return payload;
    } catch (error) {
      return null;
    }
  }

  static async updateHotel(id, formData) {
    try {
      formData.updatedAt = Date.now().toString();

      const response = await axiosInstance.post(
        `${this.BASE_URL}/hotels/update/${id}`,
        formData,
        {
          headers: {
            ...this.getHeader(),
            "Content-Type": "multipart/form-data"
          },
        }
      );
      const payload = response && response.data ? response.data : response;
      
      return payload;
    } catch (error) {
      return null;
    }
  }

  static async deleteHotel(id) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/hotels/delete/${id}`,
        {},
        {
          headers: {
            ...this.getHeader(),
          },
        }
      );
      
      return response;
    } catch (error) {
      
      return null;
    }
  }

  /**ROOM */
  static async addRoom(formData) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/rooms/add`,
        formData,
        {
          headers: {
            ...this.getHeader(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const payload = response && response.data ? response.data : response;
      
      return payload;
    } catch (error) {
      return null;
    }
  }

  static async getAllAvailableRooms(hotelId) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/rooms/all-available-rooms/hotel/${hotelId}`
      );
      return response;
    } catch (error) {
      return [];
    }
  }

  static async getAvailableRoomsByDateAndType(
    checkInDate,
    checkOutDate,
    roomType
  ) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/rooms/available-rooms-by-date-and-type?checkInDate=${checkInDate}&checkOutDate=${checkOutDate}&roomType=${roomType}`
      );

      return response;
    } catch (error) {
      return [];
    }
  }

  static async getRoomTypes() {
    try {
      const response = await axiosInstance.get(`${this.BASE_URL}/rooms/types`);
      
      return response;
    } catch (error) {
      return [];
    }
  }

  static async getAllRooms(hotelId = 0) {
    try {
      let response;
      if (hotelId === 0)
        response = await axiosInstance.get(`${this.BASE_URL}/rooms/all`);
      else
        response = await axiosInstance.get(
          `${this.BASE_URL}/rooms/all/hotel/${hotelId}`
        );
      return response;
    } catch (error) {
      
      return [];
    }
  }

  static async getRoomById(roomId) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/rooms/room-by-id/${roomId}`
      );
      return response;
    } catch (error) {
      
      return null;
    }
  }

  static async getAllAdminRooms() {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/rooms/alladmin`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return [];
    }
  }

  static async deleteRoom(roomId) {
    try {
      const response = await axiosInstance.delete(
        `${this.BASE_URL}/rooms/delete/${roomId}`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return null;
    }
  }

  static async updateRoom(roomId, formData) {
    try {
      const fd = new FormData();
      for (const pair of formData.entries()) {
        fd.append(pair[0], pair[1]);
      }
      fd.append("updatedAt", Date.now().toString());

      const response = await axiosInstance.put(
        `${this.BASE_URL}/rooms/update/${roomId}`,
        fd,
        {
          headers: {
            ...this.getHeader(),
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const payload = response && response.data ? response.data : response;

      return payload;
    } catch (error) {
      return null;
    }
  }

  /**BOOKING */
  static async bookRoom(booking) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/bookings/book-room`,
        booking,
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;

      return payload;
    } catch (error) {
      return null;
    }
  }

  static async bookAdminRoom(booking) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/bookings/admin/book-room`,
        booking,
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;
      
      return payload;
    } catch (error) {
      return null;
    }
  }

  static async getAllBookings() {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/bookings/all`,
        {
          headers: this.getHeader(),
        }
      );
      return response;
    } catch (error) {
      return [];
    }
  }

  static async getBookingByConfirmationCode(bookingCode) {
    try {
      const response = await axiosInstance.get(
        `${this.BASE_URL}/bookings/get-booking-by-confirmation-code/${bookingCode}`,
        { headers: this.getHeader() }
      );
      
      return response;
    } catch (error) {
      return null;
    }
  }

  static async cancelBooking(bookingId) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/bookings/cancel/${bookingId}`,
        {},
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;
      
      return payload;
    } catch (error) {
      return null;
    }
  }

  static async completeBooking(bookingId) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/bookings/complete/${bookingId}`,
        {},
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;

      return payload;
    } catch (error) {
      return null;
    }
  }

  static async confirmBooking(bookingId) {
    try {
      const response = await axiosInstance.post(
        `${this.BASE_URL}/bookings/confirm/${bookingId}`,
        {},
        {
          headers: this.getHeader(),
        }
      );
      const payload = response && response.data ? response.data : response;

      return payload;
    } catch (error) {
      return null;
    }
  }

  /**AUTHENTICATION CHECKER */
  static logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
  }
  static isAuthenticated() {
    const token = localStorage.getItem("token");
    return !!token;
  }
  static isAdmin() {
    const role = localStorage.getItem("role");
    return role === "ADMIN" || role === "SUPERADMIN";
  }
  static isUser() {
    const role = localStorage.getItem("role");
    return role === "USER";
  }
  static isSuperAdmin() {
    const role = localStorage.getItem("role");
    return role === "SUPERADMIN";
  }
}
